// src/pages/customer-panel/settings/AppointmentSettingsSection.js
// Appointment booking settings component for the customer panel settings page.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    TextField,
    Switch,
    FormControlLabel,
    Button,
    IconButton,
    Chip,
    Paper,
    Alert,
    CircularProgress,
    Checkbox,
    Snackbar,
    Collapse,
    Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axiosInstance from '../../../utils/axiosInstance';
import { useChatbots } from '../../../contexts/ChatbotContext';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const inputSx = {
    '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' },
    '& .MuiInputLabel-root': { fontFamily },
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': { borderColor: '#10b981' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#10b981' },
};

const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' },
};

const generateSlots = (start, end, duration) => {
    const slots = [];
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let cur = sh * 60 + sm;
    const endMin = eh * 60 + em;
    while (cur + duration <= endMin) {
        const sEnd = cur + duration;
        const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
        slots.push({ start: fmt(cur), end: fmt(sEnd), max_bookings: 1 });
        cur = sEnd;
    }
    return slots;
};

const DEFAULT_AVAILABILITY = {
    mon: { enabled: true, start: '09:00', end: '17:00', slots: generateSlots('09:00', '17:00', 60) },
    tue: { enabled: true, start: '09:00', end: '17:00', slots: generateSlots('09:00', '17:00', 60) },
    wed: { enabled: true, start: '09:00', end: '17:00', slots: generateSlots('09:00', '17:00', 60) },
    thu: { enabled: true, start: '09:00', end: '17:00', slots: generateSlots('09:00', '17:00', 60) },
    fri: { enabled: true, start: '09:00', end: '17:00', slots: generateSlots('09:00', '17:00', 60) },
    sat: { enabled: false, start: '09:00', end: '17:00', slots: generateSlots('09:00', '17:00', 60) },
    sun: { enabled: false, start: '09:00', end: '17:00', slots: generateSlots('09:00', '17:00', 60) },
};

const DAY_LABELS = {
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday',
    sun: 'Sunday',
};

const DEFAULT_SETTINGS = {
    is_booking_enabled: false,
    default_duration_minutes: 30,
    buffer_before_minutes: 0,
    buffer_after_minutes: 0,
    slot_duration_minutes: 60,
    max_bookings_per_slot: 1,
    min_notice_hours: 1,
    max_days_ahead: 30,
    weekly_availability: { ...DEFAULT_AVAILABILITY },
    holidays: [],
    special_days: [],
};

export default function AppointmentSettingsSection() {
    const navigate = useNavigate();
    const { activeChatbot } = useChatbots();
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [calendarConnected, setCalendarConnected] = useState(null); // null=loading, true/false

    // Inline form visibility
    const [showAddHoliday, setShowAddHoliday] = useState(false);
    const [showAddSpecialDay, setShowAddSpecialDay] = useState(false);

    // New holiday form state
    const [newHoliday, setNewHoliday] = useState({ date: '', name: '', recurring: false });
    // New special day form state
    const [newSpecialDay, setNewSpecialDay] = useState({ date: '', name: '', start: '09:00', end: '17:00' });
    // Expanded days for slot editing
    const [expandedDays, setExpandedDays] = useState({});

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/api/appointment-settings');
            if (res.data) {
                const duration = res.data.slot_duration_minutes || 60;
                const serverAvail = res.data.weekly_availability || {};
                // Merge and ensure each day has a slots array
                const mergedAvail = {};
                Object.keys(DAY_LABELS).forEach((day) => {
                    const src = serverAvail[day] || DEFAULT_AVAILABILITY[day];
                    mergedAvail[day] = {
                        ...DEFAULT_AVAILABILITY[day],
                        ...src,
                        slots: src.slots && src.slots.length > 0
                            ? src.slots
                            : generateSlots(src.start || '09:00', src.end || '17:00', duration),
                    };
                });
                setSettings({
                    ...DEFAULT_SETTINGS,
                    ...res.data,
                    slot_duration_minutes: duration,
                    max_bookings_per_slot: res.data.max_bookings_per_slot || 1,
                    weekly_availability: mergedAvail,
                    holidays: res.data.holidays || [],
                    special_days: res.data.special_days || [],
                });
            }
            setError(null);
        } catch (err) {
            console.error('Failed to fetch appointment settings:', err);
            setError('Failed to load appointment settings.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    // Check Google Calendar connection status
    useEffect(() => {
        axiosInstance.get('/api/google-calendar/status')
            .then((res) => setCalendarConnected(res.data?.connected === true))
            .catch(() => setCalendarConnected(false));
    }, []);

    const handleSave = async () => {
        try {
            setSaving(true);
            await axiosInstance.put('/api/appointment-settings', settings);
            setSnackbar({ open: true, message: 'Appointment settings saved successfully.', severity: 'success' });
        } catch (err) {
            console.error('Failed to save appointment settings:', err);
            setSnackbar({ open: true, message: 'Failed to save appointment settings.', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const updateField = (field, value) => {
        setSettings((prev) => ({ ...prev, [field]: value }));
    };

    const updateAvailability = (day, field, value) => {
        setSettings((prev) => {
            const dayData = { ...prev.weekly_availability[day], [field]: value };
            // Regenerate slots when start, end changes
            if (field === 'start' || field === 'end') {
                const start = field === 'start' ? value : dayData.start;
                const end = field === 'end' ? value : dayData.end;
                const oldSlots = dayData.slots || [];
                const newSlots = generateSlots(start, end, prev.slot_duration_minutes || 60);
                // Preserve existing max_bookings where slot times match
                dayData.slots = newSlots.map((ns) => {
                    const existing = oldSlots.find((os) => os.start === ns.start);
                    return existing ? { ...ns, max_bookings: existing.max_bookings } : ns;
                });
            }
            return {
                ...prev,
                weekly_availability: { ...prev.weekly_availability, [day]: dayData },
            };
        });
    };

    const updateSlotMaxBookings = (day, slotIndex, value) => {
        const clamped = Math.max(1, Math.min(10, parseInt(value, 10) || 1));
        setSettings((prev) => {
            const dayData = { ...prev.weekly_availability[day] };
            const slots = [...(dayData.slots || [])];
            slots[slotIndex] = { ...slots[slotIndex], max_bookings: clamped };
            dayData.slots = slots;
            return {
                ...prev,
                weekly_availability: { ...prev.weekly_availability, [day]: dayData },
            };
        });
    };

    const regenerateAllSlots = (duration) => {
        setSettings((prev) => {
            const wa = { ...prev.weekly_availability };
            Object.keys(wa).forEach((day) => {
                const d = { ...wa[day] };
                const oldSlots = d.slots || [];
                const newSlots = generateSlots(d.start, d.end, duration);
                d.slots = newSlots.map((ns) => {
                    const existing = oldSlots.find((os) => os.start === ns.start);
                    return existing ? { ...ns, max_bookings: existing.max_bookings } : ns;
                });
                wa[day] = d;
            });
            return { ...prev, weekly_availability: wa, slot_duration_minutes: duration };
        });
    };

    const toggleExpandDay = (day) => {
        setExpandedDays((prev) => ({ ...prev, [day]: !prev[day] }));
    };

    const addHoliday = () => {
        if (!newHoliday.date || !newHoliday.name) return;
        setSettings((prev) => ({
            ...prev,
            holidays: [...prev.holidays, { ...newHoliday }],
        }));
        setNewHoliday({ date: '', name: '', recurring: false });
        setShowAddHoliday(false);
    };

    const removeHoliday = (index) => {
        setSettings((prev) => ({
            ...prev,
            holidays: prev.holidays.filter((_, i) => i !== index),
        }));
    };

    const addSpecialDay = () => {
        if (!newSpecialDay.date || !newSpecialDay.name) return;
        setSettings((prev) => ({
            ...prev,
            special_days: [...prev.special_days, { ...newSpecialDay }],
        }));
        setNewSpecialDay({ date: '', name: '', start: '09:00', end: '17:00' });
        setShowAddSpecialDay(false);
    };

    const removeSpecialDay = (index) => {
        setSettings((prev) => ({
            ...prev,
            special_days: prev.special_days.filter((_, i) => i !== index),
        }));
    };

    if (loading || calendarConnected === null) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: GREEN_ACCENT }} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ fontFamily }}>{error}</Alert>;
    }

    // Google Calendar gate - block settings until connected
    if (calendarConnected === false) {
        return (
            <Box>
                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        borderRadius: '14px',
                        borderColor: '#f59e0b',
                        bgcolor: '#fffbeb',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: 2,
                    }}
                >
                    <WarningAmberIcon sx={{ fontSize: 48, color: '#f59e0b' }} />
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#92400e' }}>
                        Google Calendar is Not Configured
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#78350f', maxWidth: 500 }}>
                        Please set up Google Calendar integration first before configuring appointment settings.
                        This is required so that appointments are automatically synced to your calendar.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate(`/chatbot/${activeChatbot?.chatbot_identity}/agents/productivity/google-calendar`)}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            bgcolor: GREEN_ACCENT,
                            textTransform: 'none',
                            borderRadius: '10px',
                            px: 4,
                            py: 1,
                            mt: 1,
                            '&:hover': { bgcolor: '#059669' },
                        }}
                    >
                        Set Up Google Calendar
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box>
            {/* 1. Booking Toggle */}
            <FormControlLabel
                control={
                    <Switch
                        checked={settings.is_booking_enabled}
                        onChange={(e) => updateField('is_booking_enabled', e.target.checked)}
                        sx={switchSx}
                    />
                }
                label={
                    <Typography sx={{ fontFamily, fontWeight: 500 }}>
                        Enable Appointment Booking
                    </Typography>
                }
                sx={{ mb: 3 }}
            />

            {/* 2. Weekly Availability */}
            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1rem', mb: 2 }}>
                Weekly Availability
            </Typography>
            <Box sx={{ mb: 4 }}>
                {Object.keys(DAY_LABELS).map((day) => {
                    const dayData = settings.weekly_availability[day];
                    const slots = dayData.slots || [];
                    const isExpanded = expandedDays[day];
                    return (
                        <Box key={day} sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    py: 1,
                                }}
                            >
                                <Typography sx={{ fontFamily, fontWeight: 500, width: 100, flexShrink: 0 }}>
                                    {DAY_LABELS[day]}
                                </Typography>
                                <Switch
                                    checked={dayData.enabled}
                                    onChange={(e) => updateAvailability(day, 'enabled', e.target.checked)}
                                    size="small"
                                    sx={switchSx}
                                />
                                <TextField
                                    type="time"
                                    size="small"
                                    value={dayData.start}
                                    onChange={(e) => updateAvailability(day, 'start', e.target.value)}
                                    disabled={!dayData.enabled}
                                    sx={{ ...inputSx, width: 150 }}
                                    inputProps={{ step: 300 }}
                                />
                                <Typography sx={{ fontFamily, color: 'text.secondary' }}>to</Typography>
                                <TextField
                                    type="time"
                                    size="small"
                                    value={dayData.end}
                                    onChange={(e) => updateAvailability(day, 'end', e.target.value)}
                                    disabled={!dayData.enabled}
                                    sx={{ ...inputSx, width: 150 }}
                                    inputProps={{ step: 300 }}
                                />
                                {dayData.enabled && slots.length > 0 && (
                                    <Tooltip title={isExpanded ? 'Hide slots' : `Show ${slots.length} slots`}>
                                        <IconButton size="small" onClick={() => toggleExpandDay(day)}>
                                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {dayData.enabled && (
                                    <Chip
                                        icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                                        label={`${slots.length} slots`}
                                        size="small"
                                        sx={{
                                            fontFamily,
                                            fontSize: '0.7rem',
                                            height: 24,
                                            bgcolor: `${GREEN_ACCENT}15`,
                                            color: GREEN_ACCENT,
                                            fontWeight: 600,
                                        }}
                                    />
                                )}
                            </Box>
                            <Collapse in={isExpanded && dayData.enabled}>
                                <Box sx={{ pl: '116px', pb: 2, pt: 0.5 }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {slots.map((slot, idx) => (
                                            <Paper
                                                key={idx}
                                                variant="outlined"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    px: 1.5,
                                                    py: 0.75,
                                                    borderRadius: '10px',
                                                    borderColor: 'grey.200',
                                                    bgcolor: '#fafafa',
                                                }}
                                            >
                                                <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#334155', minWidth: 90 }}>
                                                    {slot.start} - {slot.end}
                                                </Typography>
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    value={slot.max_bookings}
                                                    onChange={(e) => updateSlotMaxBookings(day, idx, e.target.value)}
                                                    inputProps={{ min: 1, max: 10, style: { textAlign: 'center', padding: '4px 0', width: 36, fontFamily, fontSize: '0.8rem' } }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': { borderRadius: '6px' },
                                                        '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: GREEN_ACCENT },
                                                    }}
                                                />
                                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: 'text.secondary' }}>
                                                    max
                                                </Typography>
                                            </Paper>
                                        ))}
                                    </Box>
                                    {slots.length === 0 && (
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: 'text.secondary' }}>
                                            No slots. Adjust the start/end time or slot duration.
                                        </Typography>
                                    )}
                                </Box>
                            </Collapse>
                        </Box>
                    );
                })}
            </Box>

            {/* 4. Holidays */}
            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1rem', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon sx={{ fontSize: 20, color: GREEN_ACCENT }} />
                Holidays
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {settings.holidays.map((holiday, index) => (
                    <Paper
                        key={index}
                        variant="outlined"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '10px',
                            borderColor: 'grey.200',
                        }}
                    >
                        <Typography sx={{ fontFamily, fontSize: '0.875rem', fontWeight: 500 }}>
                            {holiday.date}
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.875rem', color: 'text.secondary' }}>
                            {holiday.name}
                        </Typography>
                        {holiday.recurring && (
                            <Chip
                                label="Recurring"
                                size="small"
                                sx={{
                                    fontFamily,
                                    fontSize: '0.7rem',
                                    height: 22,
                                    bgcolor: `${GREEN_ACCENT}15`,
                                    color: GREEN_ACCENT,
                                    fontWeight: 600,
                                }}
                            />
                        )}
                        <IconButton size="small" onClick={() => removeHoliday(index)} sx={{ ml: 0.5 }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Paper>
                ))}
                {settings.holidays.length === 0 && !showAddHoliday && (
                    <Typography sx={{ fontFamily, fontSize: '0.875rem', color: 'text.secondary' }}>
                        No holidays configured.
                    </Typography>
                )}
            </Box>
            {showAddHoliday ? (
                <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: '10px', mb: 2, borderColor: 'grey.200' }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Date"
                                type="date"
                                fullWidth
                                size="small"
                                value={newHoliday.date}
                                onChange={(e) => setNewHoliday((prev) => ({ ...prev, date: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                sx={inputSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Holiday Name"
                                fullWidth
                                size="small"
                                value={newHoliday.name}
                                onChange={(e) => setNewHoliday((prev) => ({ ...prev, name: e.target.value }))}
                                sx={inputSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={newHoliday.recurring}
                                        onChange={(e) => setNewHoliday((prev) => ({ ...prev, recurring: e.target.checked }))}
                                        sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT } }}
                                    />
                                }
                                label={<Typography sx={{ fontFamily, fontSize: '0.875rem' }}>Recurring</Typography>}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={addHoliday}
                                    disabled={!newHoliday.date || !newHoliday.name}
                                    sx={{
                                        fontFamily,
                                        bgcolor: GREEN_ACCENT,
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        '&:hover': { bgcolor: '#059669' },
                                    }}
                                >
                                    Add
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setShowAddHoliday(false);
                                        setNewHoliday({ date: '', name: '', recurring: false });
                                    }}
                                    sx={{ fontFamily, textTransform: 'none', color: 'text.secondary' }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            ) : (
                <Button
                    startIcon={<AddIcon />}
                    onClick={() => setShowAddHoliday(true)}
                    sx={{
                        fontFamily,
                        textTransform: 'none',
                        color: GREEN_ACCENT,
                        mb: 3,
                        '&:hover': { bgcolor: `${GREEN_ACCENT}10` },
                    }}
                >
                    Add Holiday
                </Button>
            )}

            {/* 5. Special Days */}
            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1rem', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarMonthIcon sx={{ fontSize: 20, color: GREEN_ACCENT }} />
                Special Days
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {settings.special_days.map((day, index) => (
                    <Paper
                        key={index}
                        variant="outlined"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '10px',
                            borderColor: 'grey.200',
                        }}
                    >
                        <Typography sx={{ fontFamily, fontSize: '0.875rem', fontWeight: 500 }}>
                            {day.date}
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.875rem', color: 'text.secondary' }}>
                            {day.name}
                        </Typography>
                        <Chip
                            label={`${day.start} - ${day.end}`}
                            size="small"
                            sx={{
                                fontFamily,
                                fontSize: '0.7rem',
                                height: 22,
                                bgcolor: `${GREEN_ACCENT}15`,
                                color: GREEN_ACCENT,
                                fontWeight: 600,
                            }}
                        />
                        <IconButton size="small" onClick={() => removeSpecialDay(index)} sx={{ ml: 0.5 }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Paper>
                ))}
                {settings.special_days.length === 0 && !showAddSpecialDay && (
                    <Typography sx={{ fontFamily, fontSize: '0.875rem', color: 'text.secondary' }}>
                        No special days configured.
                    </Typography>
                )}
            </Box>
            {showAddSpecialDay ? (
                <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: '10px', mb: 2, borderColor: 'grey.200' }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Date"
                                type="date"
                                fullWidth
                                size="small"
                                value={newSpecialDay.date}
                                onChange={(e) => setNewSpecialDay((prev) => ({ ...prev, date: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                sx={inputSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Name"
                                fullWidth
                                size="small"
                                value={newSpecialDay.name}
                                onChange={(e) => setNewSpecialDay((prev) => ({ ...prev, name: e.target.value }))}
                                sx={inputSx}
                            />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField
                                label="Start"
                                type="time"
                                fullWidth
                                size="small"
                                value={newSpecialDay.start}
                                onChange={(e) => setNewSpecialDay((prev) => ({ ...prev, start: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                sx={inputSx}
                                inputProps={{ step: 300 }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField
                                label="End"
                                type="time"
                                fullWidth
                                size="small"
                                value={newSpecialDay.end}
                                onChange={(e) => setNewSpecialDay((prev) => ({ ...prev, end: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                sx={inputSx}
                                inputProps={{ step: 300 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={addSpecialDay}
                                    disabled={!newSpecialDay.date || !newSpecialDay.name}
                                    sx={{
                                        fontFamily,
                                        bgcolor: GREEN_ACCENT,
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        '&:hover': { bgcolor: '#059669' },
                                    }}
                                >
                                    Add
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setShowAddSpecialDay(false);
                                        setNewSpecialDay({ date: '', name: '', start: '09:00', end: '17:00' });
                                    }}
                                    sx={{ fontFamily, textTransform: 'none', color: 'text.secondary' }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            ) : (
                <Button
                    startIcon={<AddIcon />}
                    onClick={() => setShowAddSpecialDay(true)}
                    sx={{
                        fontFamily,
                        textTransform: 'none',
                        color: GREEN_ACCENT,
                        mb: 3,
                        '&:hover': { bgcolor: `${GREEN_ACCENT}10` },
                    }}
                >
                    Add Special Day
                </Button>
            )}

            {/* 6. Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : null}
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        bgcolor: GREEN_ACCENT,
                        textTransform: 'none',
                        borderRadius: '10px',
                        px: 4,
                        py: 1,
                        '&:hover': { bgcolor: '#059669' },
                    }}
                >
                    {saving ? 'Saving...' : 'Save Settings'}
                </Button>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
